import { logger } from '@/ui/utilities/instrumentation/logger'
import {
	calendarConnectionIndexQueryKey,
	calendarOAuthRedirect,
	type CalendarProvider,
	useCalendarConnectionDestroy,
	useCalendarConnectionIndex,
} from '@jobilla/nexus-sdk'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

/**
 * Drives the account calendar page: the user's single connection (if any),
 * the full-page OAuth connect handoff, and disconnect.
 */
export function useCalendarConnections() {
	const queryClient = useQueryClient()
	const { data, isLoading, isError } = useCalendarConnectionIndex()
	const destroy = useCalendarConnectionDestroy()

	const [connectingProvider, setConnectingProvider] = useState<CalendarProvider | null>(null)
	const [connectFailed, setConnectFailed] = useState(false)
	const [disconnectFailed, setDisconnectFailed] = useState(false)

	// One calendar per user — the list holds at most one row.
	const connection = data?.[0] ?? null

	const connect = async (provider: CalendarProvider) => {
		setConnectFailed(false)
		setConnectingProvider(provider)

		try {
			const { data: redirect } = await calendarOAuthRedirect(provider)
			// Hand the browser off to the provider's consent screen. The page
			// unloads here, so there's no state to reset on success.
			window.location.assign(redirect.authorizationUrl)
		} catch (error) {
			logger.warn('Calendar connection could not be started', {
				category: 'calendar',
				data: { provider },
			})
			logger.error(error, { category: 'calendar' })
			setConnectFailed(true)
			setConnectingProvider(null)
		}
	}

	const disconnect = async (id: number) => {
		setDisconnectFailed(false)

		try {
			// The delete route's path param is a string; the connection id is numeric.
			await destroy.mutateAsync({ id: String(id) })
			await queryClient.invalidateQueries({ queryKey: calendarConnectionIndexQueryKey() })
		} catch (error) {
			logger.warn('Calendar disconnect failed', {
				category: 'calendar',
				data: { connectionId: id },
			})
			logger.error(error, { category: 'calendar' })
			setDisconnectFailed(true)
		}
	}

	return {
		connection,
		isLoading,
		isError,
		connect,
		connectingProvider,
		connectFailed,
		disconnect,
		disconnectFailed,
		isDisconnecting: destroy.isPending,
	}
}
